import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { extend, reverse, sortBy, groupBy } from 'lodash';

import withViewportWidth from 'decorators/withViewportWidth';

import { GoogleMap } from 'components/core/library/map';
import { ZoomControls } from 'components/core/Map';
import GoogleMaps from 'lib/googleMaps';
import { isLarge } from 'utils/breakpoints';
import { locationsToBounds } from 'utils/locationsToBounds';
import toLatLng from 'utils/toLatLng';

import FacilityMarkers from './FacilityMarkers';
import SearchOnMoveControl from './SearchOnMoveControl';

export function withGoogleOnMount(WrappedComponent) {
	return function WithGoogleOnMount(props) {
		const [google, setGoogle] = React.useState(null);

		React.useEffect(() => {
			let isMounted = true;
			const { google: googlePromise } = GoogleMaps.getInstance({
				key: props.googleMapsKey,
			});
			googlePromise.then((g) => {
				if (isMounted) setGoogle(g);
			});
			return () => {
				isMounted = false;
			};
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

		if (!google) return null;

		return (
			<WrappedComponent
				{...props}
				google={google}
			/>
		);
	};
}

export function withMapFacilities(WrappedComponent) {
	function WithMapFacilities(props) {
		// Derived facilities data (withPropsOnChange(['facilities'], ...))
		const computedFacilities = React.useMemo(() => {
			let facilities = props.facilities.map((facility) =>
				extend(facility, { location: toLatLng(facility.location) }),
			);

			facilities = groupBy(
				reverse(
					sortBy(
						facilities.filter((f) => f.inNetwork),
						'location.lat',
					),
				),
				(f) => f.isSelfStorage && 'storage',
			);

			return facilities.storage || [];
		}, [props.facilities]);

		// State (withStateHandlers)
		const [currentZoom, setCurrentZoom] = React.useState(props.initialZoom);
		const [loading, setLoading] = React.useState(true);
		const [showRedoButton, setShowRedoButton] = React.useState(false);
		const [searchOnMoveState, setSearchOnMove] = React.useState(
			props.searchOnMove,
		);
		const [mapBounds, setMapBounds] = React.useState(null);

		// State handlers
		const zoomIn = React.useCallback(
			() => setCurrentZoom((z) => z + 1),
			[],
		);
		const zoomOut = React.useCallback(
			() => setCurrentZoom((z) => z - 1),
			[],
		);
		const handleZoomChange = React.useCallback(
			(zoom) => setCurrentZoom(zoom),
			[],
		);
		const handleIdle = React.useCallback(() => {
			props.onReady();
			return setLoading(false);
		}, [props.onReady]);
		const toggleSearchOnMove = React.useCallback(
			() => setSearchOnMove((s) => !s),
			[],
		);

		const handleRedoSearch = React.useCallback(() => {
			props.onRedoSearch(mapBounds);
			setShowRedoButton(false);
			setSearchOnMove(true);
		}, [mapBounds, props.onRedoSearch]);

		// Bounds/center computation (withPropsOnChange(['google', 'facilities', 'searchLocation'], ...))
		const boundsData = React.useMemo(() => {
			const { google, searchLocation, preselectedFacilityId } = props;
			const preselectedFacility = computedFacilities.filter(
				({ id }) => id === preselectedFacilityId,
			);
			if (google && computedFacilities.length) {
				const initialBounds = locationsToBounds(
					google,
					preselectedFacility.length
						? preselectedFacility
						: computedFacilities,
				);
				const centerObj =
					preselectedFacilityId && preselectedFacility.length
						? locationsToBounds(
								google,
								preselectedFacility,
							).getCenter()
						: initialBounds.getCenter();
				const center = { lat: centerObj.lat(), lng: centerObj.lng() };

				return { initialBounds, center };
			}
			return { center: searchLocation };
		}, [
			props.google,
			computedFacilities,
			props.searchLocation,
			props.preselectedFacilityId,
		]);

		// Override search on move on smaller screens (withPropsOnChange(['breakpoint', 'searchOnMove', 'isNewSearchPage'], ...))
		const effectiveSearchOnMove = React.useMemo(() => {
			return !props.isNewSearchPage && !isLarge(props.viewportWidth)
				? false
				: searchOnMoveState;
		}, [props.viewportWidth, searchOnMoveState, props.isNewSearchPage]);

		// Bounds change handler (withHandlers)
		const handleBoundsChange = React.useCallback(
			(bounds) => {
				if (effectiveSearchOnMove) {
					props.onRedoSearch(bounds);
				}

				setMapBounds(bounds);
				setShowRedoButton(!effectiveSearchOnMove);
			},
			[effectiveSearchOnMove, props.onRedoSearch],
		);

		return (
			<WrappedComponent
				{...props}
				facilities={computedFacilities}
				currentZoom={currentZoom}
				loading={loading}
				showRedoButton={showRedoButton}
				searchOnMove={effectiveSearchOnMove}
				mapBounds={mapBounds}
				zoomIn={zoomIn}
				zoomOut={zoomOut}
				handleZoomChange={handleZoomChange}
				handleIdle={handleIdle}
				toggleSearchOnMove={toggleSearchOnMove}
				setMapBounds={setMapBounds}
				setRedoButton={setShowRedoButton}
				handleRedoSearch={handleRedoSearch}
				{...boundsData}
				handleBoundsChange={handleBoundsChange}
			/>
		);
	}

	return withViewportWidth(WithMapFacilities);
}

export function Map({
	google,
	center,
	currentZoom,
	handleZoomChange,
	handleBoundsChange,
	handleIdle,
	facilities,
	breakpoint,
	loading,
	zoomIn,
	zoomOut,
	showRedoButton,
	searchOnMove,
	toggleSearchOnMove,
	handleRedoSearch,
	initialBounds,
	phone,
	highlightedFacility,
	getLocalPhoneNumberByZipCode,
	preselectedFacilityId,
	isNewSearchPage = false,
	onMarkerHover,
	onMarkerLeave,
	onScrollToFacility,
	searchParams,
	searchType,
	baseUrl,
	onShowMapFacilityCard,
}) {
	return (
		<div className="map-container">
			<GoogleMap
				center={center}
				initialBounds={initialBounds}
				zoom={currentZoom}
				onZoomChanged={handleZoomChange}
				onBoundsChange={handleBoundsChange}
				onIdle={handleIdle}
				google={google}
				preselectedFacilityId={preselectedFacilityId}
			>
				<FacilityMarkers
					facilities={facilities}
					highlightedFacility={highlightedFacility}
					breakpoint={breakpoint}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
					preselectedFacilityId={preselectedFacilityId}
					onMarkerHover={onMarkerHover}
					onMarkerLeave={onMarkerLeave}
					onScrollToFacility={onScrollToFacility}
					searchParams={searchParams}
					searchType={searchType}
					baseUrl={baseUrl}
					onShowMapFacilityCard={onShowMapFacilityCard}
					isNewSearchPage={isNewSearchPage}
				/>
			</GoogleMap>
			<div className={classnames('map-control-container', { loading })}>
				{!isNewSearchPage && (
					<div className="search-on-move-box">
						<SearchOnMoveControl
							showRedoButton={showRedoButton}
							searchOnMove={searchOnMove}
							onToggleSearchOnMove={toggleSearchOnMove}
							onRedoSearch={handleRedoSearch}
						/>
					</div>
				)}

				<ZoomControls
					onZoomIn={zoomIn}
					onZoomOut={zoomOut}
					className={isNewSearchPage && 'new-search-page-zoom'}
				/>
			</div>
		</div>
	);
}

Map.propTypes = {
	zoomIn: PropTypes.func,
	zoomOut: PropTypes.func,
	handleZoomChange: PropTypes.func,
	handleBoundsChange: PropTypes.func,
	center: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
	}),
	currentZoom: PropTypes.number,
	handleIdle: PropTypes.func,
	facilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	showRedoButton: PropTypes.bool,
	searchOnMove: PropTypes.bool,
	toggleSearchOnMove: PropTypes.func,
	handleRedoSearch: PropTypes.func,
	initialBounds: PropTypes.object,
	google: PropTypes.object,
	breakpoint: PropTypes.string,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	highlightedFacility: PropTypes.number,
	preselectedFacilityId: PropTypes.number,
	isNewSearchPage: PropTypes.bool,
	onMarkerHover: PropTypes.func,
	onMarkerLeave: PropTypes.func,
	onScrollToFacility: PropTypes.func,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	baseUrl: PropTypes.string,
	onShowMapFacilityCard: PropTypes.func,
};

export const enhance = (Component) =>
	withGoogleOnMount(withMapFacilities(Component));

export default enhance(Map);
